import React, { useState } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import {
  LINKED_ACCOUNT_DETAIL_ROUTE,
  PROD_LINKED_ACCOUNTS_PATH,
  TEST_LINKED_ACCOUNTS_PATH,
} from "../../../router/RouterUtils";
import LinkedAccountDetailPage from "./detail-page/LinkedAccountDetailPage";
import LinkedAccountsPage from "./LinkedAccountsPage";
import TestLinkedAccountsTab from "./TestLinkedAccountsTab";
import { usePaginatedRequest } from "../../shared/hooks/usePaginatedRequest";
import { LinkedAccount } from "../../../models/Entities";
import useAppContext from "../../context/useAppContext";
import { MergeFlagFeature, useMergeFlag } from "../../shared/hooks/useMergeFlag";
import LinkedAccountsRedesignPage from "./redesign/LinkedAccountsRedesignPage";

/**
 * Either shows a detail page for an individual linked account, the linked
 * accounts page for prod accounts, or the Linked Accounts page for test
 * accounts.
 */
const LinkedAccountsTab = () => {
  const location = useLocation();
  const { user } = useAppContext();
  const { enabled: isRedesignEnabled, hasInitialized: hasRedesignFlagResolved } = useMergeFlag({
    feature: MergeFlagFeature.MERGE_FLAG_LINKED_ACCOUNTS_TABLE_REDESIGN,
    organizationId: user.organization.id,
  });
  const [linkedAccountsParamsPath, setLinkedAccountsParamsPath] = useState<string>();

  const onLinkedAccountsParamsPathChange = (paramsPath: string) => {
    setLinkedAccountsParamsPath(paramsPath);
  };

  const {
    results: linkedAccounts,
    hasNext,
    hasPrevious,
    onNext,
    onPrevious,
    onRemove,
    refetch,
    isLoading,
  } = usePaginatedRequest<LinkedAccount>({
    rootPath: "/integrations/linked-accounts",
    paramsPath: linkedAccountsParamsPath,
    skip: isRedesignEnabled !== false,
  });
  if (!hasRedesignFlagResolved) {
    return null;
  }

  if (isRedesignEnabled === true) {
    return (
      <Switch>
        <Route path={LINKED_ACCOUNT_DETAIL_ROUTE}>
          <LinkedAccountDetailPage />
        </Route>
        <Route exact path={PROD_LINKED_ACCOUNTS_PATH}>
          <LinkedAccountsRedesignPage key="production" isTestAccounts={false} />
        </Route>
        <Route exact path={TEST_LINKED_ACCOUNTS_PATH}>
          <LinkedAccountsRedesignPage key="test" isTestAccounts />
        </Route>
        <Redirect to={{ pathname: PROD_LINKED_ACCOUNTS_PATH, search: location.search }} />
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path={LINKED_ACCOUNT_DETAIL_ROUTE}>
        <LinkedAccountDetailPage />
      </Route>
      <Route exact path={PROD_LINKED_ACCOUNTS_PATH}>
        <LinkedAccountsPage
          onLinkedAccountsParamsPathChange={onLinkedAccountsParamsPathChange}
          isTestAccounts={false}
          linkedAccounts={linkedAccounts}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onNext={onNext}
          onPrevious={onPrevious}
          onRemove={onRemove}
          onRefetch={refetch}
          isLoading={isLoading}
        />
      </Route>
      <Route path={TEST_LINKED_ACCOUNTS_PATH}>
        <TestLinkedAccountsTab
          onLinkedAccountsParamsPathChange={onLinkedAccountsParamsPathChange}
          isTestAccounts
          linkedAccounts={linkedAccounts}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onNext={onNext}
          onPrevious={onPrevious}
          onRemove={onRemove}
          onRefetch={refetch}
          isLoading={isLoading}
        />
      </Route>

      <Redirect to={PROD_LINKED_ACCOUNTS_PATH} />
    </Switch>
  );
};

export default LinkedAccountsTab;
